import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { getQuestionnaireAnswers } from "@medplum/core";
import { QuestionnaireResponse } from "@medplum/fhirtypes";
import { Loading, useMedplum } from "@medplum/react";

import { Group, Paper, Stack, Title } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import { NewbornPostpartumVitalsSignQuestionsLinkIds } from "@/encounters/constants/newborn-postpartum";
import { fetchAmendmentHistoryByQuestionnaireUrl } from "@/encounters/services/questionnaire-response";
import {
  formatTemperatureDisplay,
  isVitalValuePresentForDisplay,
} from "@/encounters/utils/assessments-vitals";
import { Questionnaires } from "@/fhir/enums/questionnaires";
import NotFound from "@/NotFound";

const VitalsSignDiff = ({ currentVersionIndex }: { currentVersionIndex: number }) => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const medplum = useMedplum();

  const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
  const [currentVersion, setCurrentVersion] = useState<QuestionnaireResponse>();
  const [previousVersion, setPreviousVersion] = useState<QuestionnaireResponse>();

  useEffect(() => {
    const getAmendmentHistory = async () => {
      medplum.invalidateSearches("QuestionnaireResponse");
      try {
        const [foundAmendmentHistory] = await fetchAmendmentHistoryByQuestionnaireUrl(
          medplum,
          Questionnaires.NewbornPostpartumVitalsSign,
          encounterId!,
        );

        if (!foundAmendmentHistory) return;

        setCurrentVersion(foundAmendmentHistory[currentVersionIndex]);
        setPreviousVersion(foundAmendmentHistory[currentVersionIndex + 1]);
      } finally {
        setIsFetchingInitialData(false);
      }
    };
    getAmendmentHistory();
  }, [currentVersionIndex, encounterId, medplum]);

  if (isFetchingInitialData) return <Loading />;

  if (!currentVersion || !previousVersion) return <NotFound />;

  const previousSizeAnswers = getQuestionnaireAnswers(previousVersion);
  const currentSizeAnswers = getQuestionnaireAnswers(currentVersion);

  const previousColor =
    previousSizeAnswers[NewbornPostpartumVitalsSignQuestionsLinkIds.Color]?.valueCoding?.display ||
    "";
  const currentColor =
    currentSizeAnswers[NewbornPostpartumVitalsSignQuestionsLinkIds.Color]?.valueCoding?.display ||
    "";
  const previousJaundice =
    previousSizeAnswers[NewbornPostpartumVitalsSignQuestionsLinkIds.Jaundice]?.valueCoding
      ?.display || "";
  const currentJaundice =
    currentSizeAnswers[NewbornPostpartumVitalsSignQuestionsLinkIds.Jaundice]?.valueCoding
      ?.display || "";
  const previousHeartRate =
    previousSizeAnswers[NewbornPostpartumVitalsSignQuestionsLinkIds.HeartRate]?.valueInteger;
  const currentHeartRate =
    currentSizeAnswers[NewbornPostpartumVitalsSignQuestionsLinkIds.HeartRate]?.valueInteger;
  const previousRespiratoryRate =
    previousSizeAnswers[NewbornPostpartumVitalsSignQuestionsLinkIds.RespiratoryRate]?.valueInteger;
  const currentRespiratoryRate =
    currentSizeAnswers[NewbornPostpartumVitalsSignQuestionsLinkIds.RespiratoryRate]?.valueInteger;
  const previousTemperature =
    previousSizeAnswers[NewbornPostpartumVitalsSignQuestionsLinkIds.Temperature]?.valueDecimal;
  const currentTemperature =
    currentSizeAnswers[NewbornPostpartumVitalsSignQuestionsLinkIds.Temperature]?.valueDecimal;
  const previousTemperatureUnit =
    previousSizeAnswers[NewbornPostpartumVitalsSignQuestionsLinkIds.TemperatureUnit]?.valueString ||
    "";
  const currentTemperatureUnit =
    currentSizeAnswers[NewbornPostpartumVitalsSignQuestionsLinkIds.TemperatureUnit]?.valueString ||
    "";
  const previousNotes =
    previousSizeAnswers[NewbornPostpartumVitalsSignQuestionsLinkIds.Notes]?.valueString || "";
  const currentNotes =
    currentSizeAnswers[NewbornPostpartumVitalsSignQuestionsLinkIds.Notes]?.valueString || "";
  const previousStool =
    previousSizeAnswers[NewbornPostpartumVitalsSignQuestionsLinkIds.Stool]?.valueString || "";
  const currentStool =
    currentSizeAnswers[NewbornPostpartumVitalsSignQuestionsLinkIds.Stool]?.valueString || "";
  const previousVoid =
    previousSizeAnswers[NewbornPostpartumVitalsSignQuestionsLinkIds.Void]?.valueString || "";
  const currentVoid =
    currentSizeAnswers[NewbornPostpartumVitalsSignQuestionsLinkIds.Void]?.valueString || "";
  const previousTemperatureMethod =
    previousSizeAnswers[NewbornPostpartumVitalsSignQuestionsLinkIds.TemperatureMethod]?.valueCoding
      ?.display || "";
  const currentTemperatureMethod =
    currentSizeAnswers[NewbornPostpartumVitalsSignQuestionsLinkIds.TemperatureMethod]?.valueCoding
      ?.display || "";

  const previousTemperatureValue = formatTemperatureDisplay(
    previousTemperature?.toString(),
    previousTemperatureUnit,
    previousTemperatureMethod,
  );
  const currentTemperatureValue = formatTemperatureDisplay(
    currentTemperature?.toString(),
    currentTemperatureUnit,
    currentTemperatureMethod,
  );

  return (
    <Paper p="md" withBorder>
      <Stack mb="lg">
        <Title order={2}>Vitals</Title>
      </Stack>
      <Group grow align="stretch">
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Previous
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>Heart Rate</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={
                    isVitalValuePresentForDisplay(currentHeartRate) ? `${currentHeartRate} bpm` : ""
                  }
                  previous={
                    isVitalValuePresentForDisplay(previousHeartRate)
                      ? `${previousHeartRate} bpm`
                      : ""
                  }
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Temperature</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentTemperatureValue}
                  previous={previousTemperatureValue}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Respiratory Rate</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={
                    isVitalValuePresentForDisplay(currentRespiratoryRate)
                      ? `${currentRespiratoryRate} bpm`
                      : ""
                  }
                  previous={
                    isVitalValuePresentForDisplay(previousRespiratoryRate)
                      ? `${previousRespiratoryRate} bpm`
                      : ""
                  }
                />
              </Stack>

              <Stack gap="0">
                <TextWrapper fw={600}>Color</TextWrapper>
                <WordsDiff showRemoved current={currentColor} previous={previousColor} />
              </Stack>

              <Stack gap="0">
                <TextWrapper fw={600}>Jaundice</TextWrapper>
                <WordsDiff showRemoved current={currentJaundice} previous={previousJaundice} />
              </Stack>

              <Stack gap="0">
                <TextWrapper fw={600}>Void</TextWrapper>
                <WordsDiff showRemoved current={currentVoid} previous={previousVoid} />
              </Stack>

              <Stack gap="0">
                <TextWrapper fw={600}>Stool</TextWrapper>
                <WordsDiff showRemoved current={currentStool} previous={previousStool} />
              </Stack>

              <Stack gap="0">
                <TextWrapper fw={600}>Notes</TextWrapper>
                <WordsDiff showRemoved current={currentNotes} previous={previousNotes} />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Amended
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>Heart Rate</TextWrapper>
                <WordsDiff
                  current={
                    isVitalValuePresentForDisplay(currentHeartRate) ? `${currentHeartRate} bpm` : ""
                  }
                  previous={
                    isVitalValuePresentForDisplay(previousHeartRate)
                      ? `${previousHeartRate} bpm`
                      : ""
                  }
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Temperature</TextWrapper>
                <WordsDiff current={currentTemperatureValue} previous={previousTemperatureValue} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Respiratory Rate</TextWrapper>
                <WordsDiff
                  current={
                    isVitalValuePresentForDisplay(currentRespiratoryRate)
                      ? `${currentRespiratoryRate} bpm`
                      : ""
                  }
                  previous={
                    isVitalValuePresentForDisplay(previousRespiratoryRate)
                      ? `${previousRespiratoryRate} bpm`
                      : ""
                  }
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Color</TextWrapper>
                <WordsDiff current={currentColor} previous={previousColor} />
              </Stack>

              <Stack gap="0">
                <TextWrapper fw={600}>Jaundice</TextWrapper>
                <WordsDiff current={currentJaundice} previous={previousJaundice} />
              </Stack>

              <Stack gap="0">
                <TextWrapper fw={600}>Void</TextWrapper>
                <WordsDiff current={currentVoid} previous={previousVoid} />
              </Stack>

              <Stack gap="0">
                <TextWrapper fw={600}>Stool</TextWrapper>
                <WordsDiff current={currentStool} previous={previousStool} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Notes</TextWrapper>
                <WordsDiff current={currentNotes} previous={previousNotes} />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      </Group>
    </Paper>
  );
};

export default VitalsSignDiff;
